import React, { useEffect, useMemo, useState } from 'react';

import { makeStyles } from '@mui/styles';

import { Selectable, SelectableInfo } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { Employee, Organization } from '@vestahealthcare/common/models';

import {
  FilterItem,
  FilterSelectable,
  Label,
  Select,
  SelectLanguage,
  TextInput,
} from 'styleguide-v2';
import { FilterEmployeeOptions } from 'styleguide-v2/src/components/SelectAssignee';

import { ReferralDropdown } from 'dash/src/components/ReferralDropdown';
import { GetVisitSummaryParams } from 'dash/src/services/VisitServices';
import { getFilterItem } from 'dash/src/utils/filterUtils';

import { useDebounce } from '../../../utils/debounceUtils';

type KeyGetVisitSummaryParams = keyof GetVisitSummaryParams;

type Props = {
  data: {
    appointmentTypes: Selectable[];
    employees: SelectableInfo<Employee>[];
    languages: Selectable[];
    referralSources: SelectableInfo<Organization>[];
    pods: Selectable[];
  };
  filters?: {
    [x in KeyGetVisitSummaryParams]?: FilterItem;
  };
  loading?: boolean;
  onChange: (
    filters: {
      [x in KeyGetVisitSummaryParams]?: FilterItem;
    },
  ) => void;
};

const useStyles = makeStyles({
  minutesLabel: {
    '&&': {
      alignSelf: 'center',
      flex: '0 0 fit-content',
      marginLeft: '1rem',
      minWidth: 0,
    },
  },
  minutes: {
    '&&': {
      minWidth: 0,
      flex: '0 0 6rem',
    },
  },
});

export const VisitSummaryFilterBar = ({
  data,
  filters,
  loading,
  onChange,
}: Props) => {
  const styles = useStyles();

  const defaultFrom = useMemo(
    () => (filters?.ccmMinutesFrom?.value as FilterSelectable)?.value as string,
    [filters?.ccmMinutesFrom],
  );
  const defaultTo = useMemo(
    () => (filters?.ccmMinutesTo?.value as FilterSelectable)?.value as string,
    [filters?.ccmMinutesTo],
  );

  const [fromMinutes, setFromMinutes] = useState<string>();
  const [toMinutes, setToMinutes] = useState<string>();

  const fromDebounce = useDebounce(fromMinutes, 500);
  const toDebounce = useDebounce(toMinutes, 500);

  useEffect(() => {
    let ccmMinutesFrom: FilterItem | undefined;
    let ccmMinutesTo: FilterItem | undefined;
    if (fromDebounce) {
      const number =
        fromDebounce !== undefined ? parseInt(fromDebounce, 10) : NaN;
      ccmMinutesFrom = getFilterItem(
        translate('visitsSummary.filter.ccmMinutesFrom'),
        isNaN(number) ? undefined : number,
      );
    }
    if (toDebounce) {
      const number = toDebounce !== undefined ? parseInt(toDebounce, 10) : NaN;
      ccmMinutesTo = getFilterItem(
        translate('visitsSummary.filter.ccmMinutesTo'),
        isNaN(number) ? undefined : number,
      );
    }
    if (defaultFrom !== fromDebounce || defaultTo !== toDebounce) {
      onChange({
        ...filters,
        ccmMinutesFrom,
        ccmMinutesTo,
      });
    }
  }, [fromDebounce, toDebounce]);

  useEffect(() => {
    setFromMinutes(defaultFrom);
    setToMinutes(defaultTo);
  }, [filters?.ccmMinutesFrom, filters?.ccmMinutesTo]);

  return (
    <>
      <Select
        data-cy="visit-summary-filter-bar-pod"
        items={data.pods}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            podId: getFilterItem(translate('visitsSummary.filter.pod'), values),
          })
        }
        placeholder={translate('visitsSummary.filter.pod')}
        placeholderV2
        size="xs"
        value={filters?.podId?.value || []}
      />
      <Select
        data-cy="visit-summary-filter-bar-engagementOwnerId"
        filterOptions={FilterEmployeeOptions}
        items={data.employees.filter((e) => e?.info?.isEngagementCoordinator)}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            engagementOwnerId: getFilterItem(
              translate('visitsSummary.filter.engagementOwnerId'),
              values,
            ),
          })
        }
        placeholder={translate('visitsSummary.filter.engagementOwnerId')}
        placeholderV2
        size="xs"
        value={filters?.engagementOwnerId?.value || []}
      />
      <ReferralDropdown
        data-cy="visit-summary-filter-bar-referral"
        items={data.referralSources}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) => {
          onChange({
            ...filters,
            referralSourceId: getFilterItem(
              translate('visitsSummary.filter.referralSource'),
              values,
            ),
          });
        }}
        placeholder={translate('visitsSummary.filter.referralSource')}
        placeholderV2
        size="xs"
        value={filters?.referralSourceId?.value || []}
      />
      <SelectLanguage
        data-cy="visit-summary-filter-bar-language"
        items={data.languages}
        limitTags={1}
        loading={loading}
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            language: getFilterItem(
              translate('visitsSummary.filter.language'),
              values,
            ),
          })
        }
        placeholder={translate('visitsSummary.filter.language')}
        placeholderV2
        size="xs"
        value={(filters?.language?.value as Selectable[]) || []}
      />
      <Select
        className="large"
        data-cy="visit-summary-filter-bar-excluded-prio-appointments"
        items={data.appointmentTypes}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            excludeAppointmentTypesInPriorMonth: getFilterItem(
              translate(
                'visitsSummary.filter.excludeAppointmentTypesInPriorMonth',
              ),
              values,
            ),
          })
        }
        placeholder={translate(
          'visitsSummary.filter.excludeAppointmentTypesInPriorMonth',
        )}
        placeholderV2
        size="xs"
        value={filters?.excludeAppointmentTypesInPriorMonth?.value || []}
      />
      <Label className={styles.minutesLabel} noMargin>
        {translate('visitsSummary.filter.ccmMinutes')}:
      </Label>
      <TextInput
        className={styles.minutes}
        maxLength={4}
        onChange={setFromMinutes}
        placeholder={translate('global.from')}
        value={fromMinutes}
        size="xs"
      />
      <TextInput
        className={styles.minutes}
        maxLength={4}
        onChange={setToMinutes}
        placeholder={translate('global.to')}
        value={toMinutes}
        size="xs"
      />
    </>
  );
};
